import type { Appointment } from "@dentacore/shared-types";
import { isConfirmedStatus } from "@dentacore/shared-types";

import { categoryColour } from "../plans/categories-dialog";
import { clockOfMinutes } from "./grid-geometry";
import { procedureNames } from "./group-visits";
import styles from "./day-grid.module.css";

/**
 * **What a booking card says** (CF-D3, CF-D4) — the same on every calendar,
 * one location or several.
 *
 * Name first, with a circle before it — **filled is confirmed, hollow is not**
 * (anything checked in, in the chair or finished counts as confirmed) — and a
 * **New** tag for a first visit. Then `start–end · who or where · what`. In a
 * narrow column (several locations, or two bookings side by side) it drops to
 * the location badge, the circle and the start, with the first name under it.
 *
 * The colour is set by `cardColour` on the card itself: the procedure's
 * category tints the fill and draws the border, heavier on the left; a first
 * visit and a booking with no category keep a neutral border.
 */
export function CardBody({
  appointment,
  startMinute,
  endMinute,
  detail,
  badge,
  compact,
  roomForDetail,
  stateLabel,
}: {
  appointment: Appointment;
  startMinute: number;
  endMinute: number;
  /** The middle of the second line: the chair in a doctor's column, the doctor in a chair's. */
  detail: string | null;
  /** The location badge, when several locations are shown. */
  badge?: React.ReactNode;
  compact: boolean;
  /** Tall enough for a second line. */
  roomForDetail: boolean;
  /** A state worth saying in words — "In the chair", "Awaiting validation". */
  stateLabel?: string;
}): React.JSX.Element {
  const patient = appointment.patient;
  const confirmed = isConfirmedStatus(appointment.status);
  const isNew = appointment.isFirstVisit === true;
  // Every procedure named (FV-D6): "Check-up + Filling".
  const what = procedureNames(appointment);
  const times = `${clockOfMinutes(startMinute)}–${clockOfMinutes(endMinute)}`;
  const second = [times, detail, what].filter(
    (part): part is string => part !== null && part !== "",
  );

  if (patient === null) {
    return (
      <>
        {badge}
        <span className={styles.blockTitle}>
          <span data-testid="appointment-block">Chair held</span>
        </span>
        <span className={roomForDetail && !compact ? styles.blockSub : styles.srOnly}>
          {second.join(" · ")}
        </span>
      </>
    );
  }

  // A family visit (FV-D15): the first named, the rest counted, all in the tooltip.
  const party = appointment.party ?? [];
  const others = party.length;
  const partyNames = party
    .flatMap((member) =>
      member.patient === null ? [] : [`${member.patient.firstName} ${member.patient.lastName}`],
    )
    .join(", ");

  const circle = (
    <i
      className={confirmed ? `${styles.conf} ${styles.confOn}` : styles.conf}
      aria-hidden="true"
      data-testid="confirmation-circle"
      data-confirmed={confirmed ? "true" : "false"}
    />
  );
  const said = `${confirmed ? "Confirmed" : "Not confirmed"}${isNew ? ", first visit" : ""}${
    stateLabel === undefined ? "" : `, ${stateLabel}`
  }`;

  if (compact) {
    return (
      <>
        <span className={styles.blockTitle}>
          {badge}
          {circle}
          {clockOfMinutes(startMinute)}
          {isNew && <em className={styles.newTag}>N</em>}
        </span>
        {roomForDetail && (
          <span className={styles.compactName}>
            {patient.firstName}
            {others > 0 && <em className={styles.partyCount}> +{others}</em>}
          </span>
        )}
        <span className={styles.srOnly}>
          {patient.firstName} {patient.lastName}
          {others > 0 ? `, with ${partyNames}` : ""} · {second.join(" · ")} · {said}
        </span>
      </>
    );
  }

  return (
    <>
      {badge}
      <span
        className={
          badge === undefined
            ? `${styles.blockTitle} ${styles.titleRow}`
            : `${styles.blockTitle} ${styles.titleRow} ${styles.titleBesideBadge}`
        }
      >
        {circle}
        {/* The name gives way, never the tags beside it (CZ-D3: the New tag is never cut). */}
        <span className={styles.titleName}>
          {patient.firstName} {patient.lastName}
        </span>
        {others > 0 && (
          <em className={styles.partyTag} title={partyNames} data-testid="party-tag">
            +{others}
          </em>
        )}
        {isNew && (
          <em className={styles.newTag} data-testid="first-visit-tag">
            New
          </em>
        )}
      </span>
      <span className={roomForDetail ? styles.blockSub : styles.srOnly}>
        {roomForDetail ? second.join(" · ") : `${second.join(" · ")} · ${said}`}
      </span>
      {roomForDetail && <span className={styles.srOnly}>{said}</span>}
    </>
  );
}

/**
 * The card's colour, as a custom property its stylesheet mixes from. A first
 * visit is a tag, not a colour (CF-D4), so it stays neutral like a booking
 * with no category.
 */
export function cardColour(appointment: Appointment): React.CSSProperties {
  if (appointment.isFirstVisit === true || appointment.procedureCategoryColour === null) return {};
  return { "--cat": categoryColour(appointment.procedureCategoryColour) } as React.CSSProperties;
}
